/**
 * Food Product Sheet - Bottom sheet / modal for food product customization
 * Supports modifier groups, combo slots, special instructions
 */

import React, { useState, useEffect } from 'react';
import { X, Minus, Plus, Utensils, ShoppingCart } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'sonner';

const FoodProductSheet = ({ product, businessId, branchId, onClose }) => {
  const [quantity, setQuantity] = useState(1);
  const [modifierSelections, setModifierSelections] = useState({});
  const [comboSelections, setComboSelections] = useState({});
  const [specialInstructions, setSpecialInstructions] = useState('');
  const [adding, setAdding] = useState(false);

  // Helper: get unique ID for an option (POS uses local_id, not option_id)
  const optId = (opt) => opt.option_id || opt.local_id || opt.id || opt.name;
  const grpId = (grp) => grp.group_id || grp.local_id || grp.id || grp.name;
  const slotKey = (slot) => slot.slot_id || slot.local_id || slot.id || slot.name;

  // Initialize modifier defaults
  useEffect(() => {
    const mods = {};
    (product.modifier_groups || []).forEach((group) => {
      mods[grpId(group)] = [];
    });
    setModifierSelections(mods);

    const combos = {};
    (product.combo_slots || []).forEach((slot) => {
      combos[slotKey(slot)] = null;
    });
    setComboSelections(combos);
  }, [product]);

  const toggleModifier = (groupKey, option, group) => {
    const oid = optId(option);
    setModifierSelections((prev) => {
      const current = [...(prev[groupKey] || [])];
      const idx = current.findIndex((o) => optId(o) === oid);
      if (idx >= 0) {
        current.splice(idx, 1);
      } else {
        const max = group.max_selections || 999;
        if (current.length >= max) {
          if (max === 1) {
            return { ...prev, [groupKey]: [option] };
          }
          return prev;
        }
        current.push(option);
      }
      return { ...prev, [groupKey]: current };
    });
  };

  const selectCombo = (sk, option) => {
    setComboSelections((prev) => ({ ...prev, [sk]: option }));
  };

  // Calculate total price
  const basePrice = product.is_combo ? (product.combo_price || 0) : (product.pricing?.retail_price || 0);
  let modAdjustment = 0;
  Object.values(modifierSelections).forEach((opts) => {
    opts.forEach((o) => { modAdjustment += (o.price_adjustment || 0); });
  });
  let comboAdjustment = 0;
  Object.values(comboSelections).forEach((sel) => {
    if (sel) comboAdjustment += (sel.price_adjustment || 0);
  });
  const unitPrice = basePrice + modAdjustment + comboAdjustment;
  const totalPrice = unitPrice * quantity;

  // Validate required modifiers
  const isValid = () => {
    for (const group of (product.modifier_groups || [])) {
      if (group.is_required || group.min_selections > 0) {
        const selected = (modifierSelections[grpId(group)] || []).length;
        if (selected < (group.min_selections || 1)) return false;
      }
    }
    for (const slot of (product.combo_slots || [])) {
      if ((slot.min_selections || 1) > 0 && !comboSelections[slotKey(slot)]) return false;
    }
    return true;
  };

  const handleAddToCart = async () => {
    if (!isValid()) {
      toast.error('Completa las opciones requeridas');
      return;
    }
    setAdding(true);
    try {
      const customerToken = localStorage.getItem('customer_token');
      if (!customerToken) {
        toast.error('Inicia sesión para agregar al carrito');
        setAdding(false);
        return;
      }

      // Build modifier selections array
      const modSel = [];
      (product.modifier_groups || []).forEach((group) => {
        const gk = grpId(group);
        const opts = modifierSelections[gk] || [];
        if (opts.length > 0) {
          modSel.push({
            group_id: gk,
            group_name: group.name,
            options: opts.map((o) => ({
              option_id: optId(o),
              name: o.name,
              price_adjustment: o.price_adjustment || 0,
            })),
          });
        }
      });

      // Build combo selections array
      const comboSel = [];
      (product.combo_slots || []).forEach((slot) => {
        const sk = slotKey(slot);
        const sel = comboSelections[sk];
        if (sel) {
          comboSel.push({
            slot_id: sk,
            product_id: sel.product_id,
            product_name: sel.product_name || sel.name,
            price_adjustment: sel.price_adjustment || 0,
          });
        }
      });

      await api.post(`/marketplace/cart/add?customer_token=${customerToken}`, {
        product_id: product.id,
        branch_id: branchId,
        business_id: businessId,
        quantity,
        modifier_selections: modSel.length > 0 ? modSel : undefined,
        combo_selections: comboSel.length > 0 ? comboSel : undefined,
        special_instructions: specialInstructions || undefined,
        is_combo: product.is_combo || false,
      });

      toast.success('Agregado al carrito');
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al agregar');
    } finally {
      setAdding(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-end md:items-center justify-center touch-none" onClick={onClose} style={{ overscrollBehavior: 'contain' }}>
      <div
        className="bg-white w-full md:max-w-lg md:rounded-2xl rounded-t-3xl max-h-[90vh] overflow-y-auto animate-slide-up overscroll-contain"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Product Image */}
        {product.image_url ? (
          <div className="relative">
            <img src={product.image_url} alt={product.name} className="w-full h-56 md:h-64 object-cover md:rounded-t-2xl" />
            <button onClick={onClose} className="absolute top-3 right-3 bg-white/90 p-2 rounded-full shadow-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        ) : (
          <div className="relative h-40 bg-gradient-to-br from-orange-400 to-red-500 flex items-center justify-center md:rounded-t-2xl">
            <Utensils className="w-16 h-16 text-white/30" />
            <button onClick={onClose} className="absolute top-3 right-3 bg-white/90 p-2 rounded-full shadow-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        )}

        <div className="p-6 space-y-5">
          {/* Name & Price */}
          <div>
            <h2 className="text-2xl font-extrabold text-gray-900">{product.name}</h2>
            {product.description_short && (
              <p className="text-gray-500 mt-1">{product.description_short}</p>
            )}
            <p className="text-xl font-bold text-orange-600 mt-2">${basePrice.toFixed(0)}</p>
          </div>

          {/* Modifier Groups */}
          {(product.modifier_groups || []).map((group) => {
            const gk = grpId(group);
            return (
            <div key={gk} className="border-t border-gray-100 pt-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <h3 className="font-bold text-gray-900">{group.name}</h3>
                  <p className="text-xs text-gray-500">
                    {group.is_required ? 'Obligatorio' : 'Opcional'}
                    {group.max_selections === 1 ? ' · Elige 1' : group.max_selections ? ` · Máx ${group.max_selections}` : ''}
                  </p>
                </div>
                {group.is_required && (modifierSelections[gk] || []).length === 0 && (
                  <span className="text-xs bg-red-100 text-red-600 px-2 py-1 rounded-full">Requerido</span>
                )}
              </div>
              <div className="space-y-2">
                {(group.options || []).map((option) => {
                  const oid = optId(option);
                  const isSelected = (modifierSelections[gk] || []).some((o) => optId(o) === oid);
                  return (
                    <button
                      key={oid}
                      onClick={() => toggleModifier(gk, option, group)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                        isSelected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                        </div>
                        <span className={`text-sm ${isSelected ? 'font-medium text-gray-900' : 'text-gray-700'}`}>{option.name}</span>
                      </div>
                      {option.price_adjustment > 0 && (
                        <span className="text-sm text-gray-500">+${option.price_adjustment}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
          })}

          {/* Combo Slots */}
          {product.is_combo && (product.combo_slots || []).map((slot) => {
            const sk = slotKey(slot);
            return (
            <div key={sk} className="border-t border-gray-100 pt-4">
              <h3 className="font-bold text-gray-900 mb-3">{slot.name}</h3>
              <div className="space-y-2">
                {(slot.options || []).map((option) => {
                  const comboSel = comboSelections[sk];
                  const isSelected = comboSel && (comboSel.product_id || comboSel.name) === (option.product_id || option.name);
                  return (
                    <button
                      key={option.product_id || option.local_id || option.name}
                      onClick={() => selectCombo(sk, option)}
                      className={`w-full flex items-center justify-between p-3 rounded-xl border transition-all ${
                        isSelected ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          isSelected ? 'border-orange-500 bg-orange-500' : 'border-gray-300'
                        }`}>
                          {isSelected && <div className="w-2 h-2 bg-white rounded-full" />}
                        </div>
                        <span className={`text-sm ${isSelected ? 'font-medium' : ''}`}>{option.product_name || option.name}</span>
                      </div>
                      {option.price_adjustment > 0 && (
                        <span className="text-sm text-gray-500">+${option.price_adjustment}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          );
          })}

          {/* Special Instructions */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="font-bold text-gray-900 mb-2">Instrucciones especiales</h3>
            <textarea
              value={specialInstructions}
              onChange={(e) => setSpecialInstructions(e.target.value)}
              placeholder="Ej: sin cebolla, extra salsa, etc."
              className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm resize-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
              rows={2}
            />
          </div>

          {/* Quantity + Add to Cart */}
          <div className="border-t border-gray-100 pt-4 flex items-center gap-4">
            <div className="flex items-center gap-3 bg-gray-100 rounded-xl px-3 py-2">
              <button
                onClick={() => setQuantity(Math.max(1, quantity - 1))}
                className="p-1 hover:bg-gray-200 rounded-lg transition"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="font-bold text-lg w-8 text-center">{quantity}</span>
              <button
                onClick={() => setQuantity(quantity + 1)}
                className="p-1 hover:bg-gray-200 rounded-lg transition"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
            <button
              onClick={handleAddToCart}
              disabled={adding || !isValid()}
              className="flex-1 flex items-center justify-center gap-2 bg-orange-600 text-white py-3.5 rounded-xl font-bold text-lg hover:bg-orange-700 transition disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
            >
              <ShoppingCart className="w-5 h-5" />
              {adding ? 'Agregando...' : `Agregar $${totalPrice.toFixed(0)}`}
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes slide-up {
          from { transform: translateY(100%); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-slide-up { animation: slide-up 0.3s ease-out; }
      `}</style>
    </div>
  );
};

export default FoodProductSheet;
